// Lightweight DNS-over-HTTPS utilities to detect Google Workspace MX records
// Note: Runs in the browser using public DoH providers with CORS enabled.

const googleWorkspaceMxHosts = [
  'aspmx.l.google.com.',
  'alt1.aspmx.l.google.com.',
  'alt2.aspmx.l.google.com.',
  'alt3.aspmx.l.google.com.',
  'alt4.aspmx.l.google.com.',
];

const domainCache = new Map();

export function extractDomainFromEmail(email) {
  const atIndex = email.indexOf('@');
  if (atIndex === -1) return null;
  const domain = email.slice(atIndex + 1).trim().toLowerCase();
  if (!domain || domain.includes(' ')) return null;
  return domain;
}

export function isLikelyValidEmail(email) {
  // Minimal validation; rely on the input[type=email] for UI validation
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function parseMxHosts(answer) {
  if (!answer || answer.length === 0) return [];
  return answer
    .filter((record) => record.type === 15 && typeof record.data === 'string')
    .map((record) => {
      // MX data = "priority host."
      const parts = record.data.trim().split(/\s+/);
      const host = parts.length > 1 ? parts[1] : parts[0];
      return (host || '').toLowerCase();
    })
    .filter(Boolean);
}

function hostsIndicateGoogleWorkspace(mxHosts) {
  if (mxHosts.length === 0) return false;
  for (const host of mxHosts) {
    if (googleWorkspaceMxHosts.includes(host)) return true;
    if (host.endsWith('.google.com.') || host.endsWith('.googlemail.com.')) return true;
  }
  return false;
}

async function fetchDnsMxCloudflare(domain, signal) {
  const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { accept: 'application/dns-json' },
    signal,
  });
  if (!res.ok) {
    throw new Error(`Cloudflare DoH failed: ${res.status}`);
  }
  const json = await res.json();
  return parseMxHosts(json.Answer);
}

async function fetchDnsMxGoogle(domain, signal) {
  const url = `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`;
  const res = await fetch(url, { method: 'GET', signal });
  if (!res.ok) {
    throw new Error(`Google DoH failed: ${res.status}`);
  }
  const json = await res.json();
  return parseMxHosts(json.Answer);
}

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      controller.abort();
      reject(new Error('DNS lookup timed out'));
    }, ms);
    promise
      .then((v) => {
        clearTimeout(id);
        resolve(v);
      })
      .catch((e) => {
        clearTimeout(id);
        reject(e);
      });
  });
}

export async function checkIsGoogleHostedDomain(domain) {
  const normalizedDomain = domain.trim().toLowerCase();
  if (!normalizedDomain) return false;
  if (domainCache.has(normalizedDomain)) {
    return domainCache.get(normalizedDomain);
  }

  // Consumer Gmail domains
  if (normalizedDomain === 'gmail.com' || normalizedDomain === 'googlemail.com') {
    domainCache.set(normalizedDomain, true);
    return true;
  }

  // Try Cloudflare then Google as fallback, both with short timeouts
  try {
    const c1 = new AbortController();
    const mx1 = await withTimeout(fetchDnsMxCloudflare(normalizedDomain, c1.signal), 3000, c1);
    const isGoogle1 = hostsIndicateGoogleWorkspace(mx1);
    domainCache.set(normalizedDomain, isGoogle1);
    if (isGoogle1) return true;
  } catch {
    // ignore and try fallback
  }

  try {
    const c2 = new AbortController();
    const mx2 = await withTimeout(fetchDnsMxGoogle(normalizedDomain, c2.signal), 3000, c2);
    const isGoogle2 = hostsIndicateGoogleWorkspace(mx2);
    domainCache.set(normalizedDomain, isGoogle2);
    return isGoogle2;
  } catch {
    // On failures, assume not Google-hosted to avoid false positives
    domainCache.set(normalizedDomain, false);
    return false;
  }
}

export async function isGoogleEmailForAddress(email) {
  const domain = extractDomainFromEmail(email);
  if (!domain) return false;
  // Fast-path for consumer Gmail addresses
  if (domain === 'gmail.com' || domain === 'googlemail.com') return true;
  return checkIsGoogleHostedDomain(domain);
}
